// Generated by Copilot
import { Injectable } from '@angular/core';
import { CommonConstants } from '@mpg-core/constants/common-constants';
import { CommonDataService } from '@shared/services/common-data.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {

  constructor(private commonDataService: CommonDataService) {}

  // ── Section-scoped helpers ──────────────────────────────────────────────────

  getPermissionIdForSection(sectionName: string): number | null {
    const sectionPermissions = this.commonDataService.userData.value;
    if (!Array.isArray(sectionPermissions)) return null;
    const section = sectionPermissions.find(
      (p: any) => p.sectionName === sectionName
    );
    return section?.permissionId ?? null;
  }

  hasReadWriteAccess(sectionName: string): boolean {
    return this.getPermissionIdForSection(sectionName) === CommonConstants.PERMISSION_ID_READ_WRITE;
  }

  hasReadOnlyAccess(sectionName: string): boolean {
    return this.getPermissionIdForSection(sectionName) === CommonConstants.PERMISSION_ID_READ_ONLY;
  }

  hasNoAccess(sectionName: string): boolean {
    const permissionId = this.getPermissionIdForSection(sectionName);
    return (
      permissionId === CommonConstants.PERMISSION_ID_NO_ACCESS ||
      permissionId === null ||
      permissionId === undefined
    );
  }

  hasAnyWriteAccess(): boolean {
    const sectionPermissions = this.commonDataService.userData.value;
    if (!Array.isArray(sectionPermissions)) return false;
    return sectionPermissions.some(
      (p: any) => p.permissionId === CommonConstants.PERMISSION_ID_READ_WRITE
    );
  }

  // ── Role-scoped helpers ─────────────────────────────────────────────────────

  getRoleId(): number | null {
    return this.commonDataService.userInfo.value?.userRole?.roleId ?? null;
  }

  isSuperAdmin(): boolean {
    return this.getRoleId() === CommonConstants.SUPER_ADMIN_ROLE_ID;
  }

  isFinanceExec(): boolean {
    return this.getRoleId() === CommonConstants.FINANCE_EXEC_ROLE_ID;
  }

  isFinanceCoordinator(): boolean {
    return this.getRoleId() === CommonConstants.FINANCE_COORDINATOR_ROLE_ID;
  }

  isProdAdminCoordinator(): boolean {
    return this.getRoleId() === CommonConstants.PRODUCTION_ADMIN_COORDINATOR_ROLE_ID;
  }

  isPostProdAdmin(): boolean {
    return this.getRoleId() === CommonConstants.POST_PRODUCTION_ADMIN;
  }

  // ── Admin feature gates (mirror backend @PreAuthorize) ──────────────────────

  canManageUsers(): boolean {
    const roleId = this.getRoleId();
    return (
      roleId === CommonConstants.SUPER_ADMIN_ROLE_ID ||
      roleId === CommonConstants.POST_PRODUCTION_ADMIN ||
      roleId === CommonConstants.PRODUCTION_ADMIN_COORDINATOR_ROLE_ID
    );
  }

  canUpdateProductionEntity(): boolean {
    return this.isSuperAdmin();
  }

  canChangeDeleteFlagForEntity(): boolean {
    return this.isSuperAdmin();
  }

  canSaveOrUpdatePicklist(): boolean {
    return this.isSuperAdmin();
  }

  canSaveOrUpdateVendor(): boolean {
    return this.isSuperAdmin();
  }

  canUpdateAccessRoleBased(): boolean {
    const roleId = this.getRoleId();
    return (
      roleId === CommonConstants.SUPER_ADMIN_ROLE_ID ||
      roleId === CommonConstants.PRODUCTION_ADMIN_COORDINATOR_ROLE_ID ||
      roleId === CommonConstants.POST_PRODUCTION_ADMIN
    );
  }

  canInitiateWeekendingRollback(): boolean {
    return this.isSuperAdmin();
  }

  canCopyPostProdSchedules(): boolean {
    const roleId = this.getRoleId();
    return (
      roleId === CommonConstants.SUPER_ADMIN_ROLE_ID ||
      roleId === CommonConstants.POST_PRODUCTION_ADMIN
    );
  }

}
